import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  Download,
  ArrowRight,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Upload,
  Send,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskOptions = [
  { id: "exploitation", label: "Risk of exploitation (CSE/CCE)" },
  { id: "missing", label: "Missing episodes" },
  { id: "self-harm", label: "Self-harm or mental health concerns" },
  { id: "substance", label: "Substance misuse" },
  { id: "offending", label: "Offending behaviour" },
  { id: "family", label: "Family breakdown" },
  { id: "placement", label: "Placement breakdown history" },
  { id: "education", label: "Not in education/training" },
];

const documents = [
  {
    name: "Statement of Purpose",
    description: "Overview of Epic Care services and approach",
  },
  {
    name: "Young Person Guide",
    description: "Information for young people about our services",
  },
  {
    name: "Placement Information",
    description: "Details about accommodation and support",
  },
];

export default function ReferralsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [formData, setFormData] = useState({
    referrer_name: "",
    organisation: "",
    role: "",
    email: "",
    phone: "",
    young_person_initials: "",
    age: "",
    gender: "",
    local_authority: "",
    risk_notes: "",
    current_placement_issues: "",
    preferred_move_date: "",
    consent: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRiskToggle = (riskId) => {
    setSelectedRisks((prev) =>
      prev.includes(riskId)
        ? prev.filter((id) => id !== riskId)
        : [...prev, riskId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error("Please confirm consent to submit the referral");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        risks: selectedRisks,
      };

      await axios.post(`${API}/referrals`, payload);
      
      toast.success("Referral submitted successfully! We will be in touch soon.");
      
      // Reset form
      setFormData({
        referrer_name: "",
        organisation: "",
        role: "",
        email: "",
        phone: "",
        young_person_initials: "",
        age: "",
        gender: "",
        local_authority: "",
        risk_notes: "",
        current_placement_issues: "",
        preferred_move_date: "",
        consent: false,
      });
      setSelectedRisks([]);
    } catch (error) {
      console.error("Error submitting referral:", error);
      toast.error("Failed to submit referral. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="blob-teal -top-40 -right-40" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <FileText className="w-4 h-4" />
                Make a Referral
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="referrals-headline"
              >
                Refer a young person to{" "}
                <span className="text-teal-600">Epic Care</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Complete our referral form below. We aim to respond to all
                referrals promptly and will be in touch to discuss suitability
                and next steps.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="referral-info-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  Quick Response
                </h3>
                <p className="text-slate-600 text-sm">
                  We aim to respond to all referrals within 24-48 hours
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  Secure & Confidential
                </h3>
                <p className="text-slate-600 text-sm">
                  All information is handled in accordance with data protection
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  Thorough Assessment
                </h3>
                <p className="text-slate-600 text-sm">
                  We carefully assess suitability to ensure the best match
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-12 bg-slate-50" data-testid="documents-section">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            Downloadable Documents
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {documents.map((doc) => (
              <div
                key={doc.name}
                className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">
                      {doc.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-3">
                      {doc.description}
                    </p>
                    <button
                      className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium"
                      data-testid={`download-${doc.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section className="section-padding bg-white" data-testid="referral-form-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Referral Form
              </h2>
              <p className="text-slate-600">
                Please complete all required fields marked with *
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Referrer Information */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">
                  Referrer Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="referrer_name">Your Name *</Label>
                    <Input
                      id="referrer_name"
                      name="referrer_name"
                      value={formData.referrer_name}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-referrer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organisation">Organisation *</Label>
                    <Input
                      id="organisation"
                      name="organisation"
                      value={formData.organisation}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-organisation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <Input
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-role"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Young Person Information */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">
                  Young Person Information
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="young_person_initials">
                      Young Person Initials Only *
                    </Label>
                    <Input
                      id="young_person_initials"
                      name="young_person_initials"
                      value={formData.young_person_initials}
                      onChange={handleInputChange}
                      required
                      maxLength={10}
                      className="mt-2"
                      data-testid="input-initials"
                    />
                  </div>
                  <div>
                    <Label htmlFor="age">Age (16-25) *</Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      min={16}
                      max={25}
                      value={formData.age}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-age"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, gender: value }))
                      }
                    >
                      <SelectTrigger className="mt-2" data-testid="select-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non-binary">Non-binary</SelectItem>
                        <SelectItem value="prefer-not-to-say">
                          Prefer not to say
                        </SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="local_authority">Local Authority *</Label>
                    <Input
                      id="local_authority"
                      name="local_authority"
                      value={formData.local_authority}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      data-testid="input-local-authority"
                    />
                  </div>
                </div>
              </div>

              {/* Risks */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">
                  Identified Risks
                </h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {riskOptions.map((risk) => (
                    <div key={risk.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={risk.id}
                        checked={selectedRisks.includes(risk.id)}
                        onCheckedChange={() => handleRiskToggle(risk.id)}
                        data-testid={`checkbox-risk-${risk.id}`}
                      />
                      <label
                        htmlFor={risk.id}
                        className="text-sm text-slate-700 cursor-pointer"
                      >
                        {risk.label}
                      </label>
                    </div>
                  ))}
                </div>
                <div>
                  <Label htmlFor="risk_notes">Additional Risk Notes</Label>
                  <Textarea
                    id="risk_notes"
                    name="risk_notes"
                    value={formData.risk_notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="mt-2"
                    placeholder="Please provide any additional information about risks..."
                    data-testid="textarea-risk-notes"
                  />
                </div>
              </div>

              {/* Placement Details */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">
                  Placement Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="current_placement_issues">
                      Current Placement Issues *
                    </Label>
                    <Textarea
                      id="current_placement_issues"
                      name="current_placement_issues"
                      value={formData.current_placement_issues}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="mt-2"
                      placeholder="Please describe the current situation and reasons for referral..."
                      data-testid="textarea-placement-issues"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferred_move_date">
                      Preferred Move Date (if known)
                    </Label>
                    <Input
                      id="preferred_move_date"
                      name="preferred_move_date"
                      type="date"
                      value={formData.preferred_move_date}
                      onChange={handleInputChange}
                      className="mt-2"
                      data-testid="input-move-date"
                    />
                  </div>
                </div>
              </div>

              {/* Safeguarding Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-2">
                      Safeguarding Notice
                    </h4>
                    <p className="text-amber-700 text-sm">
                      Please do not include unnecessary personal or confidential
                      data in this form. Use initials only for the young person
                      and avoid sharing sensitive information that is not
                      directly relevant to the referral.
                    </p>
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, consent: checked }))
                  }
                  data-testid="checkbox-consent"
                />
                <label htmlFor="consent" className="text-sm text-slate-700">
                  I confirm that I have the appropriate authority to make this
                  referral and that the information provided is accurate to the
                  best of my knowledge. I understand that Epic Care will contact
                  me to discuss this referral. *
                </label>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-12 h-14"
                  data-testid="submit-referral-btn"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Referral
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
